import React, { useState, useContext, useEffect, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, InputLabel, Select, MenuItem, Switch } from '@mui/material';
import LoadingSpinner from '../../components/LoadingSpinner';
import PropTypes from 'prop-types';
import {
  getUserRoles,
  getUserDetails,
  postUserDetails,
  putUserDetails,
  disableUser,
  resendUserInvite,
} from '../../api/settings';
import { StoreContext } from '../../components/Store';
import { dateFormatChange } from '../../utils/date';
import { isITUser } from '../../utils/roles';

const paginationModel = { page: 0, pageSize: 50 };

export default function DataTable({ companyId, customerTypeId }) {
  const [rowData, setRowData] = useState([]);
  const [addUser, setAddUser] = useState(false);
  const [apiError, setApiError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const store = useContext(StoreContext);

  const buttonRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [disableRoleSelection, setDisableRoleSelection] = useState(false);

  const currentCompanyId = companyId || store.eoriCompany[0] || sessionStorage.getItem('companyid');

  const disableEORIEditing = [1, '1'].includes(currentCompanyId) && !isITUser(); // only IT user can add/edit EORI users
  const isReadOnly = store.isReadOnly[0] || disableEORIEditing;

  const hideMFAField = !isITUser() || [30, 60].includes(customerTypeId);

  let input;
  const [open, setOpen] = useState(false);
  const [usersData, setUsersData] = useState({});
  const handleOnChange = (event) => {
    if (event?.$isDayjsObject) {
      setUsersData((prevData) => ({
        ...prevData,
        ['arrivalDate']: new Date(event),
      }));
    } else {
      let title = event.target.name;
      let value = event.target.value;
      setUsersData((prevData) => ({
        ...prevData,
        [title]: value,
      }));
    }
  };

  const handleSwitchChange = (event) => {
    setUsersData((prevData) => ({ ...prevData, [event.target.name]: event.target.checked }));
  };

  const apiCall = () => {
    getUserDetails(currentCompanyId)
      .then((data) => {
        store.userDetails[1](data);
        if (buttonRef.current) {
          buttonRef.current.disabled = false;
        }
        for (let i = 0; i < data.length; i++) {
          data[i]['actions'] = 'Edit';
          if (data[i]['email'] === sessionStorage.getItem('name')) {
            data[i]['name'] = sessionStorage.getItem('contactName');
          }
        }
        setRowData(data);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    async function fetchData() {
      try {
        apiCall();
        getUserRoles(currentCompanyId).then((data) => {
          store.userRoles[1](data);
        });
      } catch (err) {
        console.log(err);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (usersData?.roleId) {
      const role = store.userRoles[0].filter((role) => role.roleId === usersData.roleId);
      if (role?.[0]?.department === 'IT' && !isITUser()) {
        setDisableRoleSelection(true);
      } else {
        setDisableRoleSelection(false);
      }
    }
  }, [store.userRoles[0], usersData]);

  const handleClose = () => {
    setApiError('');
    setSuccessMessage('');
    setOpen(false);
  };
  const handleClick = (dataClickedItem) => {
    if (Object.keys(dataClickedItem).length > 0 && dataClickedItem.constructor === Object) {
      let itemsRow = dataClickedItem.row;
      for (const rowItem in itemsRow) {
        setUsersData((prevData) => ({
          ...prevData,
          [rowItem]: itemsRow[rowItem],
        }));
      }
    }
  };

  const handleEdit = async () => {
    if (buttonRef.current && !buttonRef.current.disabled) {
      buttonRef.current.disabled = true;
    }

    let dataPayload;
    let response;
    if (addUser) {
      dataPayload = {
        userEmail: usersData.email,
        roleId: input.value,
        name: usersData.name,
        ...(!hideMFAField && { mfaEnable: usersData.mfaEnable ?? true }), // Default to true if not specified
      };

      try {
        response = await postUserDetails(dataPayload, currentCompanyId);
        // Check the API's response message
        if (response?.response && response.response === 'User is already created.') {
          setApiError('User already exists');
          if (buttonRef.current) {
            buttonRef.current.disabled = false;
          }
          return; // Keep the modal open so the user can see the message
        }
        // Otherwise, assume success and proceed
        setTimeout(apiCall, 1000);
      } catch (e) {
        console.error('Error posting user details:', e);
        setApiError(e.response?.data?.message || 'An error occurred while adding the user.');
        if (buttonRef.current) {
          buttonRef.current.disabled = false;
        }
        return;
      }
    } else {
      dataPayload = {
        userEmail: usersData.email,
        roleId: input.value,
        firstname: usersData.name?.split(' ')?.[0],
        lastname: usersData.name?.split(' ')?.[1],
        ...(!hideMFAField && { mfaEnable: usersData.mfaEnable ?? true }), // Default to true if not specified
      };

      try {
        response = await putUserDetails(dataPayload, currentCompanyId);
        if (response?.response && response.response === 'User is already created.') {
          setApiError('User already exists');
          if (buttonRef.current) {
            buttonRef.current.disabled = false;
          }
          return;
        }
        setTimeout(apiCall, 1000);
      } catch (e) {
        console.error('Error updating user details:', e);
        setApiError(e.response?.data?.message || 'An error occurred while updating the user.');
        if (buttonRef.current) {
          buttonRef.current.disabled = false;
        }
        return;
      }
    }
    // If no errors, close the modal on success.
    setOpen(false);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      editable: true,
      headerClassName: 'theme--header',
    },
    {
      field: 'email',
      headerName: 'Email Address',
      flex: 1,
      editable: true,
      headerClassName: 'theme--header',
    },
    {
      field: 'lastloggedin',
      headerName: 'Last Logged In',
      type: 'number',
      flex: 1,
      editable: true,
      headerClassName: 'theme--header',
      renderCell: (params) => (
        <div>{params.row.lastloggedin ? dateFormatChange(params.row.lastloggedin, true) : ''}</div>
      ),
    },
    {
      field: 'lastmodified',
      headerName: 'Last Modified',
      type: 'number',
      flex: 1,
      editable: true,
      headerClassName: 'theme--header',
      renderCell: (params) => (
        <div>{params.row.lastmodified ? dateFormatChange(params.row.lastmodified, true) : ''}</div>
      ),
    },
    {
      field: 'disabled',
      headerName: 'Status',
      flex: 1,
      editable: true,
      headerClassName: 'theme--header',
      renderCell: (params) => (params.value === null ? 'Invited' : params.value === true ? 'Disabled' : 'Active'),
    },
  ];

  // Conditionally pushing the 'actions' column
  if (sessionStorage.getItem('role') === 'Super' && !isReadOnly) {
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      editable: true,
      headerClassName: 'theme--header',
      renderCell: (params) => (
        <span style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }} onClick={handleModalClick}>
          {params.value}
        </span>
      ),
    });
  }

  const handleModalClick = (item) => {
    console.log('clicked modal', item);
    setAddUser(false);
    setApiError('');
    setOpen(true);
  };
  const handleModalClickEmpty = () => {
    setUsersData(() => ({}));
    setOpen(true);
    setAddUser(true);
  };
  const disableUserSettings = () => {
    disableUser(usersData.email);
    try {
      setTimeout(apiCall, 1000);
    } catch (err) {
      console.log(err);
    }
    setOpen(false);
  };

  const handleResendInvite = async () => {
    try {
      setResendLoading(true);

      const nameParts = usersData.name?.trim().split(' ') || [];
      const firstname = nameParts[0] || '';

      const response = await resendUserInvite(currentCompanyId, usersData.email, firstname);

      if (response?.email === 'Password reset email sent.') {
        setApiError('');
        setSuccessMessage('Invite email resent successfully.');
        setTimeout(() => {
          apiCall();
          setSuccessMessage('');
          setResendLoading(false);
        }, 5000);
      } else {
        const errorMessage = response?.message || 'Failed to resend invite. Please try again.';
        setApiError(errorMessage);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to resend invite. Please try again.';
      setApiError(errorMessage);
    }
  };

  return (
    <>
      <Paper sx={{ width: '100%' }}>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {rowData?.length > 0 && (
              <DataGrid
                rows={rowData}
                columns={columns}
                getRowId={(row) => row.userId}
                initialState={{ pagination: { paginationModel } }}
                pageSizeOptions={[20, 50, 100]}
                sx={{ border: 0 }}
                onRowClick={handleClick}
              />
            )}
            <Dialog
              open={open}
              onClose={handleClose}
              className="usersTable"
              PaperProps={{
                component: 'form',
                onSubmit: (event) => {
                  event.preventDefault();
                  const formData = new FormData(event.currentTarget);
                  const formJson = Object.fromEntries(formData.entries());
                  const email = formJson.email;
                  console.log(email);
                  handleClose();
                },
              }}
            >
              <DialogTitle>{usersData.userId ? 'Edit user' : 'Add a new user'}</DialogTitle>
              <DialogContent className="edit-add-trader-user">
                <DialogContentText>
                  {apiError && (
                    <Typography variant="body2" color="error" style={{ marginBottom: '10px' }}>
                      {apiError}
                    </Typography>
                  )}

                  {successMessage && (
                    <Typography variant="body2" color="success" style={{ marginBottom: '10px' }}>
                      {successMessage}
                    </Typography>
                  )}
                  <Box className="col-wrapper">
                    {/* Label */}
                    <Typography variant="body1" className="typography">
                      Email Address
                    </Typography>
                    {/* Input Field */}
                    <TextField
                      className="field-component"
                      value={usersData.email || ''}
                      onChange={handleOnChange}
                      name="email"
                      slotProps={{
                        input: {
                          readOnly: !addUser,
                        },
                      }}
                    />
                  </Box>
                  <Box className="col-wrapper">
                    {/* Label */}
                    <Typography variant="body1" className="typography">
                      Name
                    </Typography>
                    {/* Input Field */}
                    <TextField
                      className="field-component editable"
                      value={usersData.name || ''}
                      onChange={handleOnChange}
                      name="name"
                      slotProps={{
                        input: {
                          style: {
                            marginTop: 4,
                          },
                        },
                      }}
                    />
                  </Box>
                  <Box className="col-wrapper">
                    <InputLabel className="typography">Role</InputLabel>
                    <Select
                      labelId="submitter-label-2"
                      id="submitter-select-2"
                      value={usersData?.roleId}
                      name="roleId"
                      fullWidth
                      className="field-component"
                      onChange={handleOnChange}
                      inputRef={(node) => {
                        input = node;
                      }}
                      disabled={disableRoleSelection}
                    >
                      {store.userRoles[0].map((item) => {
                        return (
                          <MenuItem value={item.roleId} key={item.roleId}>
                            {item.department !== 'Haulier' && item.department.replace('-', ' ')}
                            &nbsp;
                            {item.department !== '360PAY-STAFF' && item.role}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </Box>
                  {!hideMFAField && (
                    <Box className="col-wrapper">
                      <InputLabel className="typography">Enable MFA</InputLabel>
                      <Switch name="mfaEnable" checked={usersData?.mfaEnable ?? true} onChange={handleSwitchChange} />
                    </Box>
                  )}
                  {!addUser && (
                    <>
                      <Box className="col-wrapper">
                        <InputLabel className="typography">Status</InputLabel>
                        <div className="columnterms">
                          {usersData.disabled !== null && (
                            <div className="readonly-label">{usersData.disabled === true ? 'Disabled' : 'Active'}</div>
                          )}
                          {usersData.disabled === null && <div className="readonly-label">Invited</div>}
                        </div>
                      </Box>

                      <Box className="col-wrapper">
                        <InputLabel className="typography">Last Logged In:</InputLabel>
                        <div className="readonly-label">
                          {usersData.lastloggedin ? dateFormatChange(usersData.lastloggedin, true) : ''}
                        </div>
                      </Box>
                    </>
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ padding: '24px', justifyContent: 'space-between' }}>
                {!addUser && usersData.disabled !== null && (
                  <Button className="button-class deny-button" onClick={disableUserSettings}>
                    {!usersData.disabled ? 'Disable' : 'Enable'} this user
                  </Button>
                )}

                {!addUser && usersData.disabled === null && (
                  <Button onClick={handleResendInvite} disabled={resendLoading}>
                    Resend Invite
                  </Button>
                )}

                <Button onClick={handleEdit} className="button-class" ref={buttonRef}>
                  Save and Close
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Paper>
      <div className="hyperlinks">
        {sessionStorage.getItem('role') === 'Super' && !isReadOnly && (
          <Button
            style={{ marginTop: '1rem', marginBottom: '1rem' }}
            className={'button-class'}
            onClick={handleModalClickEmpty}
          >
            Add a New User
          </Button>
        )}
      </div>
    </>
  );
}

DataTable.propTypes = {
  companyId: PropTypes.string,
};
